import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { distinctUntilChanged, filter, first, map, Observable, switchMap, tap } from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElementId, getFormGroupLikeWrapperMainFormId } from '@blaze-runtime/engine-domain';
import { DataBindingSourceType, ElementQueryState } from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { DatasetRow, WorkflowSchema } from '@blaze-shared/schema-domain';
import { isDeepEqual, isNullishOrEmptyObject } from '@blaze-shared/shared-util';
import { Workflow, WorkflowPhase } from '@blaze-shared/workflow-domain';

import { FormListRowsService } from '../../../../elements/form-list/form-list-rows.service';
import { ElementStoreViewService } from '../../../../services/element-store-view.service';
import { RowGenerationService } from '../../../../services/row-generation.service';
import { hasWrapperRefs } from '../../../element-model/element-props-guards';
import {
  isEditableForm,
  isFormGroupLike,
  isFormList,
  isFormListLike,
} from '../../../element-model/element-type-guards';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';
import { fillCustomFields, getFormCustomFields } from './utils/form-custom-fields.utils';
import { SortResultsService } from './utils/sort-results.service';
import { WizardDraftService } from './utils/wizard-draft.service';

interface ContextDataProviderPreResult {
  workflow: Workflow | undefined;
  schema: WorkflowSchema | undefined;
  output: DatasetRow[];
  elementQuery: ElementQueryState | undefined;
}

@Injectable({ providedIn: 'root' })
export class ContextDataProvider implements DataSourceProvider {
  private readonly formListRowsService = inject(FormListRowsService);
  private readonly rowGenerationService = inject(RowGenerationService);
  private readonly sortResultsService = inject(SortResultsService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly store = inject(Store);
  private readonly wizardDraftService = inject(WizardDraftService);
  readonly sourceType = 'context' as const;

  getDataStream({
    source,
    element,
    binding,
    expression,
  }: DataProviderQuery): Observable<InputDataPackage> {
    const workflowId = source.id;
    return this.elementStoreView.entities$.pipe(
      first(),
      switchMap((elements) =>
        this.wizardDraftService.getWizardFormDraft(
          element,
          (form) => isFormList(form) && form.props.content.origin === 'wizard',
          elements,
        ),
      ),
      switchMap((wizardDraft) => {
        if (wizardDraft) {
          return this.store.select(runtimeSelectors.selectWorkflowsSchemaById(workflowId)).pipe(
            first(),
            map((schema) => ({
              sourceType: 'context' as DataBindingSourceType,
              schema,
              data: wizardDraft,
            })),
          );
        }
        return this.store
          .select(runtimeSelectors.selectWorkflowsSchemaWithOutputAndStatusById(workflowId))
          .pipe(
            filter(
              ({ output, schema, status }) =>
                output !== undefined &&
                ((!schema?.isComputed && status?.phase !== WorkflowPhase.Running) ||
                  status?.phase === WorkflowPhase.BlockedWithData),
            ),
            concatLatestFrom(() => [
              this.store.select(runtimeSelectors.selectWorkflowById(workflowId)),
              this.wizardDraftService.getWizardDraft(element),
              this.store.select(runtimeSelectors.selectQueryByElementId(element.id)),
            ]),
            map(([{ output, schema }, workflow, wizardDraft, elementQuery]) => {
              const result: ContextDataProviderPreResult = {
                workflow,
                schema,
                output,
                elementQuery,
              };

              if (!binding) {
                return result;
              }
              const hasNoData = !output?.length;

              //#region form-like & formgroup-like handling
              const isForm = isEditableForm(element);
              const isAddFormGroup =
                isFormGroupLike(element) && element.props.content.action === 'add';
              const isUpsertFormGroup =
                isFormGroupLike(element) && element.props.content.action === 'edit';
              const isInsertForm = isForm && element.props.content.action === 'insert';
              const isCustomForm = isForm && element.props.content.action === 'custom';
              if (
                isAddFormGroup ||
                isInsertForm ||
                (hasNoData && (isForm || isUpsertFormGroup || isCustomForm))
              ) {
                const customFields = !isCustomForm
                  ? undefined
                  : getFormCustomFields(element.props.content?.model);
                result.output = [
                  this.rowGenerationService.getNewRow(binding.dataSource, { customFields }),
                ];

                if (!expression && isAddFormGroup && isNullishOrEmptyObject(wizardDraft)) {
                  this.updateElementOutputIfDiff(element.id, result.output);
                  if (hasWrapperRefs(element)) {
                    const { mainFormId } = getFormGroupLikeWrapperMainFormId(
                      element.props.wrapper?.refs,
                    );
                    if (mainFormId) {
                      this.updateElementOutputIfDiff(mainFormId, result.output[0]);
                    }
                  }
                }
              } else if (!hasNoData && isCustomForm) {
                const customFields = getFormCustomFields(element.props.content?.model);
                if (customFields?.length) {
                  result.output = produce(result.output, (draft) => {
                    fillCustomFields(draft[0], customFields);
                  });
                }
              }
              //#endregion
              return result;
            }),
            map(({ output, schema, workflow, elementQuery }) => {
              const data = this.sortResultsService.sortIfApplicable(
                element?.type,
                binding,
                output,
                undefined,
                workflow,
              );
              return {
                sourceType: 'context',
                data,
                schema,
                elementQuery,
              } satisfies InputDataPackage;
            }),
            distinctUntilChanged(isDeepEqual),
            tap(() => {
              if (isFormListLike(element)) {
                this.formListRowsService.removeEmptyishRows(element.id);
              }
            }),
          );
      }),
    );
  }

  private updateElementOutputIfDiff(elementId: CanvasElementId, data: any): void {
    const currentData = syncSelect(
      this.store,
      runtimeSelectors.selectOutputUiDataById(elementId, 'change'),
    );
    if (!data || isDeepEqual(currentData, data)) {
      return;
    }

    this.store.dispatch(
      runtimeActions.updateRuntimeUiOutput({
        elementId,
        eventType: 'change',
        data,
        updateSource: 'provider',
      }),
    );
  }
}
